/**
 * Obstruction Sprite Generator
 *
 * SDF sprite generation for the obstruction anchor symbol layer. Each of the
 * 6 `ObstructionType` values renders to a single composite sprite: a solid
 * circle disc with the corresponding Tabler icon cut out of the middle
 * (same `destination-out` technique as task composite sprites).
 *
 * Sprite keys are exposed via `OBSTRUCTION_TYPE_SPRITE_KEYS` in
 * `@/constants/obstructionMapColors` so the layer's `icon-image` `match`
 * expression can reference them directly without re-importing the local
 * mapping.
 *
 * @module components/map/symbolSprites/obstructionSprites
 */

import { renderToStaticMarkup } from 'react-dom/server';
import { createElement } from 'react';
import {
  IconAlertTriangle,
  IconBarrierBlock,
  IconBulldozer,
  IconCalendarEvent,
  IconCarCrash,
  IconTool,
  type TablerIcon,
} from '@tabler/icons-react';

import { ObstructionType } from '@/types/obstructions';
import { OBSTRUCTION_TYPE_SPRITE_KEYS } from '@/constants/obstructionMapColors';

import type { SpriteEntry } from './spriteGenerator';

// ===== SPRITE GEOMETRY CONSTANTS =====

/** Canvas size (CSS pixels) for the obstruction composite sprite. */
const OBSTRUCTION_SPRITE_SIZE = 64;

/** Padding ratio around the outer disc so the sprite has alpha margin. */
const OBSTRUCTION_DISC_PADDING_RATIO = 0.1;

/** Tabler icon size inside the composite sprite (matches the task layer's
 *  `28 / 64` proportion so all map glyphs read at the same visual weight). */
const OBSTRUCTION_ICON_SIZE = 28;

/** Tabler stroke width for the cut-out icon (matches `spriteGenerator`). */
const OBSTRUCTION_ICON_STROKE_WIDTH = 2;

/** Super-sampling factor for the obstruction sprite — matches the note layer
 *  composite (single-sprite-per-feature, can afford 4×). */
const OBSTRUCTION_SPRITE_PIXEL_RATIO = 4;

/** SDF fill — only alpha is sampled by MapLibre, but `white` is the
 *  conventional value used elsewhere in the project. */
const SDF_FILL_COLOR = 'white';

// ===== TABLER ICON MAP =====

/**
 * Per-`ObstructionType` Tabler icon component. Mirrors the user-facing icon
 * mapping in `OBSTRUCTION_TYPE_ICONS` (`@/constants/obstructions.ts`) but
 * resolves to component references so the SDF renderer can rasterise them.
 */
const OBSTRUCTION_TYPE_ICON_COMPONENTS: Record<ObstructionType, TablerIcon> = {
  [ObstructionType.Maintenance]: IconTool,
  [ObstructionType.Construction]: IconBulldozer,
  [ObstructionType.Accident]: IconCarCrash,
  [ObstructionType.Closure]: IconBarrierBlock,
  [ObstructionType.Event]: IconCalendarEvent,
  [ObstructionType.Emergency]: IconAlertTriangle,
};

// ===== CANVAS HELPERS =====

type Ctx = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

function createCanvas(
  width: number,
  height: number
): { ctx: Ctx } {
  if (typeof OffscreenCanvas !== 'undefined') {
    const canvas = new OffscreenCanvas(width, height);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Failed to get OffscreenCanvas 2D context');
    return { ctx };
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Failed to get Canvas 2D context');
  return { ctx };
}

function drawDisc(ctx: Ctx, size: number): void {
  const padding = size * OBSTRUCTION_DISC_PADDING_RATIO;
  const radius = (size - padding * 2) / 2;
  const center = size / 2;

  ctx.beginPath();
  ctx.arc(center, center, radius, 0, Math.PI * 2);
  ctx.fillStyle = SDF_FILL_COLOR;
  ctx.fill();
}

function loadIconImage(
  IconComponent: TablerIcon,
  size: number
): Promise<HTMLImageElement> {
  const svgMarkup = renderToStaticMarkup(
    createElement(IconComponent, {
      size,
      color: SDF_FILL_COLOR,
      stroke: OBSTRUCTION_ICON_STROKE_WIDTH,
    })
  );

  return new Promise((resolve, reject) => {
    const encodedSvg = encodeURIComponent(svgMarkup);
    const image = new Image();
    image.width = size;
    image.height = size;
    image.onload = () => resolve(image);
    image.onerror = () =>
      reject(new Error('Failed to load obstruction icon SVG'));
    image.src = `data:image/svg+xml;charset=utf-8,${encodedSvg}`;
  });
}

// ===== PUBLIC API =====

/**
 * Generates a single composite SDF sprite for one obstruction type. Disc fill
 * with an icon cut out via `destination-out`, super-sampled by
 * `OBSTRUCTION_SPRITE_PIXEL_RATIO`.
 */
async function generateObstructionSprite(
  type: ObstructionType
): Promise<ImageData> {
  const size = OBSTRUCTION_SPRITE_SIZE * OBSTRUCTION_SPRITE_PIXEL_RATIO;
  const iconSize = OBSTRUCTION_ICON_SIZE * OBSTRUCTION_SPRITE_PIXEL_RATIO;

  const IconComponent = OBSTRUCTION_TYPE_ICON_COMPONENTS[type];
  const iconImage = await loadIconImage(IconComponent, iconSize);

  const { ctx } = createCanvas(size, size);

  // 1. Draw outer disc fill
  drawDisc(ctx, size);

  // 2. Cut the icon out so it reads as transparent over the layer's
  //    `icon-color` (set to severity hex). Note: we colour the disc via
  //    `icon-color`; the icon shape itself reveals the basemap.
  ctx.globalCompositeOperation = 'destination-out';
  const offsetX = (size - iconSize) / 2;
  const offsetY = (size - iconSize) / 2;
  ctx.drawImage(iconImage, offsetX, offsetY, iconSize, iconSize);
  ctx.globalCompositeOperation = 'source-over';

  return ctx.getImageData(0, 0, size, size);
}

/**
 * Builds the obstruction sprite map (1 entry per `ObstructionType`). Called
 * from `generateAllSprites` so a single sprite cache backs every consumer.
 */
export async function generateObstructionSprites(): Promise<
  Map<string, SpriteEntry>
> {
  const entries = await Promise.all(
    (Object.values(ObstructionType) as ObstructionType[]).map(async (type) => {
      const data = await generateObstructionSprite(type);
      const key = OBSTRUCTION_TYPE_SPRITE_KEYS[type];
      return [
        key,
        {
          data,
          sdf: true,
          pixelRatio: OBSTRUCTION_SPRITE_PIXEL_RATIO,
        },
      ] as const;
    })
  );

  return new Map<string, SpriteEntry>(entries);
}
